import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://pastaluna.co.il";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_DESCRIPTION = "פסטה לונה (PastaLuna) פסטה איטלקית איכותית ללא גלוטן, 100% טבעונית ועשירה בחלבון. חוויה קולינרית בריאה ומזינה בכל ביס. היכנסו לאתר למגוון המוצרים.";

interface SEOProps {
  title: string;
  description?: string;
  image?: string;
  type?: "website" | "article" | "product";
  canonical?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
  /** ISO date string (e.g. "2025-04-01T12:00:00Z") for article-like pages. */
  datePublished?: string;
  /** ISO date string for last update; defaults to datePublished if omitted. */
  dateModified?: string;
}

const upsertMeta = (selector: string, attrs: Record<string, string>) => {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
  }
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

// Languages this site is available in. Add { code: "en", path: (p) => `/en${p}` }
// once English pages actually exist.
const LOCALES: { code: string; path: (p: string) => string }[] = [
  { code: "he", path: (p) => p },
];

const upsertAlternates = (pathname: string) => {
  // Remove old alternates managed by this component
  document.head
    .querySelectorAll('link[rel="alternate"][data-seo-alt="true"]')
    .forEach((el) => el.remove());
  const add = (hreflang: string, href: string) => {
    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", hreflang);
    link.setAttribute("href", href);
    link.setAttribute("data-seo-alt", "true");
    document.head.appendChild(link);
  };
  LOCALES.forEach((l) => add(l.code, `${SITE_URL}${l.path(pathname)}`));
  // x-default points at the primary (Hebrew) URL
  add("x-default", `${SITE_URL}${LOCALES[0].path(pathname)}`);
};

const SEO = ({ title, description, image, type = "website", canonical, jsonLd, datePublished, dateModified }: SEOProps) => {
  const location = useLocation();
  const url = canonical || `${SITE_URL}${location.pathname}`;
  const img = image || DEFAULT_IMAGE;
  const rawDesc = description || DEFAULT_DESCRIPTION;

  // Google SERP limits: ~60 chars for titles, ~155 for descriptions.
  // Truncate at word boundary to avoid mid-word cuts.
  const truncate = (text: string, max: number) => {
    const t = text.trim().replace(/\s+/g, " ");
    if (t.length <= max) return t;
    const cut = t.slice(0, max - 1);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
  };

  const fullTitle = truncate(title, 60);
  const desc = truncate(rawDesc, 155);

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta('meta[name="description"]', { name: "description", content: desc });

    upsertMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: desc });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: img });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "PastaLuna" });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "he_IL" });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: desc });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: img });

    upsertLink("canonical", url);
    upsertAlternates(location.pathname);

    // Article freshness signals — only meaningful for article-like pages.
    // Remove stale ones first so non-article routes don't carry them over.
    document.head
      .querySelectorAll('meta[data-seo-date="true"]')
      .forEach((el) => el.remove());
    if (datePublished) {
      const add = (property: string, content: string) => {
        const m = document.createElement("meta");
        m.setAttribute("property", property);
        m.setAttribute("content", content);
        m.setAttribute("data-seo-date", "true");
        document.head.appendChild(m);
      };
      add("article:published_time", datePublished);
      add("article:modified_time", dateModified || datePublished);
    }

    // JSON-LD structured data
    const existing = document.head.querySelector('script[data-seo-jsonld="true"]');
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "true");
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [fullTitle, desc, url, img, type, datePublished, dateModified, JSON.stringify(jsonLd)]);

  return null;
};

export default SEO;
